import React, { useState, useEffect } from 'react';
import companyApi from '../../services/companyApi';
import Notification from '../../components/notification/notification';
import { Select, Spin, Upload } from 'antd';
import {
  PageHeader,
  TWButton,
  TWModal,
  TWDataTable,
} from '../../components/ui';
import { headerCls } from '../../data/columns';
import ModalUserList from '../../components/modal-user-list/modal-user-list';
import ModalList from '../../components/modal-list/modal-list';
import ModalDelete from '../../components/modal-delete/modal-delete';
import userApi from '../../services/userApi';
import storeApi from '../../services/storeApi';
import subscriptionApi from '../../services/subscriptionApi';
import { useSelector } from 'react-redux';
import { PlusIcon } from '@heroicons/react/20/solid';
import {
  ArrowRightOnRectangleIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import CompanySkeleton from './components/company-skeleton';
import './company.css';
import { PlusOutlined, LoadingOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router';
import tokenService from '../../utils/tokenService';
import { useDispatch } from 'react-redux';
import {
  loginSuccess,
  setRole,
  setRolePermissions,
} from '../../store/slices/authSlice';
import { Link } from 'react-router-dom';
import ReactInputMask from 'react-input-mask';
import { setGlobalCompany, setCompany } from '../../store/slices/companySlice';
import { useTransition, animated, useSpringRef } from '@react-spring/web';

const cn = (...c) => c.filter(Boolean).join(' ');

const fieldClass =
  'block w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm transition placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:border-sky-400 dark:focus:ring-sky-400';

const actionIconBtn =
  'inline-flex h-9 w-9 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-slate-800 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-200';

const userHeaders = ['Name', 'Email', 'Role', 'Status'];
const userKeys = ['fullname', 'email', 'userroles', 'status'];
const storeHeaders = ['Name', 'Address', 'Manager Name', 'Manager Phone'];
const storeKeys = ['name', 'address', 'responsiblename', 'responsiblephone'];

// Active/Inactive view switch for the companies list. 'all' sends no isactive
// filter; the backend Company/List honours an optional isactive flag.
const COMPANY_STATUS_TABS = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

const Company = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { roles, user } = useSelector((state) => state.authReducer);
  const { selected } = useSelector((state) => state.companyReducer);
  const [showFilters, setShowFilters] = useState(false);
  const [statusTab, setStatusTab] = useState('active');

  const [search, setSearch] = useState({
    name: '',
    isdeleted: 'false',
  });

  const [form, setForm] = useState({
    id: '',
    logo: '',
    emaildomain: '',
    name: '',
    users: [],
    isactive: true,
    ownername: '',
    ownerphone: '',
    owneremail: '',
    subscriptionid: '',
    creditcardfeeper: 0,
    creditcardfeerate: 0,
  });

  const [loading, setLoading] = useState({
    listLoading: true,
    upsertLoading: false,
    deleteLoading: false,
    loginAsCompany: false,
  });

  const [errors, setErrors] = useState({
    emaildomain: false,
    name: false,
  });

  const [companyList, setCompanyList] = useState([]);
  const [userList, setUserList] = useState([]);
  const [subscriptionList, setSubscriptionList] = useState([]);
  const [dialogs, setDialogs] = useState({
    storeList: false,
    userList: false,
    upsert: false,
    delete: false,
  });
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedStoreList, setSelectedStoreList] = useState(null);
  const [notf, setNotf] = useState({
    display: false,
    type: 'error',
    message: '',
  });

  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 20,
    totalRecords: null,
  });

  useEffect(() => {
    const query = searchQuery();
    getList(query);
    getUserList();
    if (roles.includes(100)) {
      getSubscriptionList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.pageNumber, statusTab]);

  const getList = async (searchQuery) => {
    try {
      setLoading((prev) => ({ ...prev, listLoading: true }));
      const query = `?pageNumber=${pagination.pageNumber}&pageSize=${pagination.pageSize}&${searchQuery}`;
      const list = await companyApi.list(query);
      if (list.data.data.length > 0) {
        setCompanyList(list.data.data);
        setPagination({
          pageNumber: list.data.pageNumber,
          pageSize: list.data.pageSize,
          totalRecords: list.data.totalRecords,
        });
        dispatch(setCompany(list.data.data));
      } else {
        setCompanyList([]);
      }
    } catch (error) {
      setNotf((prev) => ({
        ...prev,
        message: error,
        display: true,
      }));
    } finally {
      setLoading((prev) => ({ ...prev, listLoading: false }));
    }
  };

  const getUserList = async () => {
    try {
      const list = await userApi.list(`?pageSize=1000&status=10&status=0`);
      if (list.data.data.length > 0) {
        setUserList(list.data.data.filter((x) => x.status !== 20));
      }
    } catch (error) {
      setNotf((prev) => ({
        ...prev,
        message: error,
        display: true,
      }));
    }
  };

  const getSubscriptionList = async () => {
    try {
      const list = await subscriptionApi.list(`?pageSize=1000`);
      if (list.data.data.length > 0) {
        setSubscriptionList(list.data.data);
      }
    } catch (error) {
      setNotf((prev) => ({
        ...prev,
        message: error,
        display: true,
      }));
    }
  };

  const onFormChange = (val, key) => {
    setForm((prev) => ({ ...prev, [key]: val }));
  };

  const onSearchChange = (val, key) => {
    setSearch((prev) => ({ ...prev, [key]: val }));
  };

  const onClickAddCompany = () => {
    setForm({
      id: '',
      logo: '',
      emaildomain: '',
      name: '',
      ownername:
        !roles.includes(100) && selected.ownername ? selected.ownername : '',
      ownerphone:
        !roles.includes(100) && selected.ownerphone ? selected.ownerphone : '',
      owneremail:
        !roles.includes(100) && selected.owneremail ? selected.owneremail : '',
      isactive: true,
      users: roles.includes(100) ? [] : [user.id],
      creditcardfeeper: 0,
      creditcardfeerate: 0,
      sendreport: true,
    });
    setDialogs((prev) => ({ ...prev, upsert: true }));
  };

  const onClickEdit = (val) => {
    setForm({
      ...val,
      imagepath: val.logo,
    });

    setDialogs((prev) => ({ ...prev, upsert: true }));
  };

  const onFormSubmit = async () => {
    if (!form.name) {
      setErrors((prev) => ({
        ...prev,
        name: !form.name ? true : false,
      }));
      return;
    } else {
      setErrors((prev) => ({ ...prev, name: false }));
    }
    // Captured before the request: `form` itself is never reassigned inside
    // this function, and this is the same signal the modal's own title and
    // submit-button label already read (`form.id ? 'Edit…' : 'Add…'`) to
    // tell a create from an edit through this one shared submit path.
    const isCreate = !form.id;
    try {
      setLoading((prev) => ({ ...prev, upsertLoading: true }));
      const body = { ...form };

      const formData = new FormData();

      for (const key of Object.keys(body)) {
        if (key === 'image' && body['image'] !== '') {
          if (body.imageid) {
            await companyApi.deleteLogoById(body.id);
          }
          formData.append('image', body.image);
        } else if (key === 'users') {
          body.users.forEach((element) => {
            formData.append('users', element);
          });
        } else {
          if (body[key] && body[key] !== 'null') {
            formData.append(key, body[key]);
          }
        }
      }

      const config = {
        headers: { 'Content-Type': 'multipart/form-data' },
      };
      const res = await companyApi.upsert(formData, config);
      if (res.data.data) {
        if (selected.id === res.data.data.id) {
          dispatch(setGlobalCompany(res.data.data));
        }
        setDialogs((prev) => ({ ...prev, upsert: false }));
        // Create only, never an edit: the server opens a Stripe account for
        // every new company, and nothing else on this screen says so. This
        // is the one place that confirmation reaches whoever just created
        // it — the refreshed list below is how they get to the company
        // itself, so this line carries no link of its own.
        if (isCreate) {
          setNotf({
            display: true,
            type: 'success',
            message: `Stripe account opened for ${res.data.data.name} — it stays on legacy payments until an admin activates direct charges.`,
          });
        }
      }
    } catch (error) {
      setNotf((prev) => ({
        ...prev,
        message: error,
        display: true,
      }));
    } finally {
      setLoading((prev) => ({ ...prev, upsertLoading: false }));
      await getList();
    }
  };
  const onCloseUpsert = () => {
    setErrors((prev) => ({ ...prev, name: false }));
    setDialogs((prev) => ({ ...prev, upsert: false }));
  };

  const onClickDelete = () => {
    setDialogs((prev) => ({ ...prev, upsert: false, delete: true }));
  };

  const onConfirmDelete = async () => {
    try {
      setLoading((prev) => ({ ...prev, deleteLoading: true }));
      await companyApi.deleteById(form.id);
      await getList();
    } catch (error) {
      setNotf((prev) => ({
        ...prev,
        message: error,
        display: true,
      }));
    } finally {
      setDialogs((prev) => ({ ...prev, delete: false, upsert: false }));
      setLoading((prev) => ({ ...prev, deleteLoading: false }));
    }
  };

  const rolePermissions = (permissions) => {
    let rolepermissions = [];
    permissions.forEach((permission) => {
      rolepermissions.push(permission.permission.code);
    });
    return rolepermissions;
  };

  const signAsCompany = async (comp) => {
    try {
      setLoading((prev) => ({ ...prev, loginAsCompany: true }));
      const res = await companyApi.signAsCompanyById(comp.id);
      if (res.data.data) {
        if (res.data.data.roles.includes('Owner')) {
          tokenService.updateLocalAccessToken(res.data.data.token);
          const userDetail = await userApi.getUserDetail();

          if (userDetail.status === 200) {
            tokenService.setRole([150]);
            dispatch(loginSuccess(userDetail.data.data));
            dispatch(setGlobalCompany(comp));
            dispatch(setRole([150]));
            dispatch(
              setRolePermissions(
                rolePermissions(res.data.data.permissions[0].rolepermissions),
              ),
            );
            navigate('/dashboard');
          }
        }
      }
    } catch (error) {
      setNotf((prev) => ({
        ...prev,
        message: error,
        display: true,
      }));
    } finally {
      setLoading((prev) => ({ ...prev, loginAsCompany: false }));
    }
  };

  const onClickShowStoreModal = async (companyId) => {
    try {
      const res = await storeApi.getByCompanyId(companyId);
      if (res.data.data) {
        setSelectedStoreList(res.data.data);
        setDialogs((prev) => ({
          ...prev,
          storeList: true,
        }));
      }
    } catch (error) {
      setNotf((prev) => ({
        ...prev,
        message: error,
        display: true,
      }));
    }
  };

  const uploadButton = (
    <div>
      {loading.upsertLoading ? <LoadingOutlined /> : <PlusOutlined />}
      <div
        className="dark:text-slate-200"
        style={{
          marginTop: 8,
        }}
      >
        Upload
      </div>
    </div>
  );

  const users = () => {
    if (
      !companyList[selectedCompany] ||
      !companyList[selectedCompany].companyusers
    ) {
      return [];
    }

    const { companyusers } = companyList[selectedCompany];

    const activeCompanyUsers = companyusers.filter(
      ({ user }) => user.status === 10,
    );

    if (roles.includes(100)) {
      return activeCompanyUsers;
    }

    if (roles.includes(150)) {
      return activeCompanyUsers.filter(({ user }) => {
        if (!user || !user.userroles || user.userroles.length === 0)
          return false;

        return !user.userroles.some(
          (userRole) =>
            userRole.role &&
            ['Admin', 'Limited Admin', 'Menu', 'Dispatchers'].includes(
              userRole.role.name,
            ),
        );
      });
    }

    return activeCompanyUsers.filter(({ user }) => {
      if (!user || !user.userroles || user.userroles.length === 0) return false;

      return !user.userroles.some(
        (userRole) =>
          userRole.role &&
          ['Admin', 'Limited Admin', 'Menu', 'Owner', 'Dispatchers'].includes(
            userRole.role.name,
          ),
      );
    });
  };

  useEffect(() => {
    transRef.start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFilters]);

  const transRef = useSpringRef();
  const transitions = useTransition(showFilters, {
    ref: transRef,
    keys: null,
    from: { maxHeight: '0px', opacity: 0 },
    enter: { maxHeight: '1000px', opacity: 1, overflowY: 'none' },
    leave: { maxHeight: '0px', opacity: 0 },
  });

  const searchQuery = () => {
    const params = { ...search };
    if (statusTab === 'active') params.isactive = 'true';
    else if (statusTab === 'inactive') params.isactive = 'false';

    const query = Object.keys(params)
      .map((key) => {
        return `${key}=${params[key]}`;
      })
      .filter(Boolean)
      .join('&');

    return query;
  };

  const onClickSearch = async () => {
    const query = searchQuery();
    await getList(query);
    setPagination((prev) => ({ ...prev, pageNumber: 1 }));
  };

  const onClickClearSearch = () => {
    setSearch({
      name: '',
      isdeleted: 'false',
    });
    getList();
  };

  const onChangeStatusTab = (tab) => {
    if (tab === statusTab) return;
    setStatusTab(tab);
    setPagination((prev) => ({ ...prev, pageNumber: 1 }));
  };

  const companyColumns = [
    {
      title: <span className={headerCls}>Logo</span>,
      key: 'logo',
      width: 64,
      render: (_, comp) =>
        comp.logo ? (
          <img
            alt=""
            className="h-9 w-9 rounded-full object-cover"
            src={comp.logo}
          />
        ) : (
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-sky-400 text-xs font-semibold uppercase text-white">
            {(comp.name || '?')
              .split(/\s+/)
              .map((w) => w[0])
              .join('')
              .slice(0, 2)}
          </span>
        ),
    },
    {
      title: <span className={headerCls}>Company Name</span>,
      key: 'name',
      render: (_, comp) =>
        roles.includes(100) ? (
          <Link
            to={comp.id}
            className="text-sm font-bold text-slate-900 hover:underline dark:text-slate-100"
          >
            {comp.name}{' '}
            {comp.isdeleted && (
              <span className="font-normal text-slate-500">(Deleted)</span>
            )}
          </Link>
        ) : (
          <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">
            {comp.name}
          </span>
        ),
    },
    {
      title: <span className={headerCls}>Owner Name</span>,
      key: 'ownername',
      render: (_, comp) => (
        <span className="text-sm text-slate-600 dark:text-slate-300">
          {comp.ownername || '—'}
        </span>
      ),
    },
    {
      title: <span className={headerCls}>Owner Phone</span>,
      key: 'ownerphone',
      render: (_, comp) => (
        <span className="text-sm text-slate-600 dark:text-slate-300">
          {comp.ownerphone || '—'}
        </span>
      ),
    },
    {
      title: <span className={headerCls}>Stores</span>,
      key: 'stores',
      render: (_, comp) => (
        <button
          type="button"
          onClick={() => onClickShowStoreModal(comp.id)}
          className="text-sm font-semibold text-blue-600 hover:text-blue-800 dark:text-sky-400 dark:hover:text-sky-300"
        >
          Show
        </button>
      ),
    },
    {
      title: <span className={headerCls}>Users</span>,
      key: 'users',
      render: (_, comp, idx) =>
        comp.companyusers.length > 0 ? (
          <button
            type="button"
            onClick={() => {
              setSelectedCompany(idx);
              setDialogs((prev) => ({ ...prev, userList: true }));
            }}
            className="text-sm font-semibold text-blue-600 hover:text-blue-800 dark:text-sky-400 dark:hover:text-sky-300"
          >
            Show
            <span className="sr-only">, {comp.name}</span>
          </button>
        ) : (
          <span className="text-sm text-slate-600 dark:text-slate-300">—</span>
        ),
    },
    {
      title: <span className="sr-only">Actions</span>,
      key: 'actions',
      align: 'right',
      width: 60,
      render: (_, comp) =>
        roles.includes(100) ? (
          <button
            type="button"
            onClick={() => signAsCompany(comp)}
            className={actionIconBtn}
            aria-label={`Open dashboard as ${comp.name}`}
          >
            <ArrowRightOnRectangleIcon className="h-5 w-5 text-blue-600 dark:text-sky-400" />
          </button>
        ) : (
          <button
            type="button"
            onClick={() => onClickEdit(comp)}
            className={actionIconBtn}
            aria-label={`Edit ${comp.name}`}
          >
            <PencilSquareIcon className="h-5 w-5" />
          </button>
        ),
    },
  ];

  return (
    <>
      <div className="company-shell min-h-[calc(100vh-4rem)] bg-white px-4 py-6 dark:bg-slate-900/40 sm:px-6 lg:px-8 lg:py-8">
        <div className="mx-auto max-w-5xl space-y-8">
          <PageHeader
            title="Companies"
            description="Create and manage companies linked to your organization."
            actions={
              <>
                <TWButton
                  variant="default"
                  icon={FunnelIcon}
                  label="Filters"
                  onClick={() => setShowFilters(!showFilters)}
                />
                <TWButton
                  variant="primary"
                  icon={PlusIcon}
                  label="Add company"
                  disabled={loading.listLoading}
                  onClick={() => onClickAddCompany()}
                />
              </>
            }
          />

          <div className="flex w-fit items-center gap-1 rounded-lg border border-slate-200 bg-slate-50 p-1 dark:border-slate-700 dark:bg-slate-800/60">
            {COMPANY_STATUS_TABS.map((tab) => (
              <button
                key={tab.value}
                type="button"
                onClick={() => onChangeStatusTab(tab.value)}
                className={cn(
                  'rounded-md px-4 py-1.5 text-sm font-medium transition',
                  statusTab === tab.value
                    ? 'bg-white text-slate-900 shadow-sm dark:bg-slate-700 dark:text-white'
                    : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200',
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {transitions(
            (style, item) =>
              item && (
                <animated.div style={{ ...style }}>
                  <section className="reveal rounded-md border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div>
                        <label
                          htmlFor="company-search-name"
                          className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400"
                        >
                          Name
                        </label>
                        <input
                          value={search.name}
                          onChange={(e) =>
                            onSearchChange(e.target.value, 'name')
                          }
                          type="text"
                          name="name"
                          id="company-search-name"
                          className={fieldClass}
                          placeholder="Search by company name"
                        />
                      </div>
                      {roles.includes(100) && (
                        <div>
                          <label
                            htmlFor="company-show-deleted"
                            className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400"
                          >
                            Show deleted
                          </label>
                          <Select
                            size="large"
                            className="company-page-select w-full"
                            style={{ width: '100%' }}
                            placeholder="Show deleted"
                            value={search.isdeleted}
                            onChange={(e) => {
                              setSearch((prev) => ({
                                ...prev,
                                isdeleted: e,
                              }));
                            }}
                          >
                            <Select.Option value={'false'}>No</Select.Option>
                            <Select.Option value={'true'}>Yes</Select.Option>
                          </Select>
                        </div>
                      )}
                    </div>
                    <div className="mt-4 flex flex-wrap justify-end gap-3">
                      <button
                        type="button"
                        onClick={() => onClickClearSearch()}
                        className="inline-flex h-9 items-center gap-2 rounded-md border border-slate-200 bg-white px-4 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-700"
                      >
                        <TrashIcon className="h-4 w-4" aria-hidden="true" />
                        Clear
                      </button>
                      <button
                        type="button"
                        onClick={() => onClickSearch()}
                        className="inline-flex h-9 items-center gap-2 rounded-md bg-blue-600 px-5 text-xs font-semibold text-white shadow-sm transition hover:bg-blue-700 dark:bg-sky-500 dark:hover:bg-sky-400"
                      >
                        <MagnifyingGlassIcon
                          className="h-4 w-4"
                          aria-hidden="true"
                        />
                        Search
                      </button>
                    </div>
                  </section>
                </animated.div>
              ),
          )}

          {loading.listLoading ? (
            <CompanySkeleton />
          ) : (
            <div className="reveal">
              <TWDataTable
                columns={companyColumns}
                data={companyList}
                rowKey="id"
                pagination={
                  pagination.totalRecords > pagination.pageSize
                    ? {
                        current: pagination.pageNumber,
                        pageSize: pagination.pageSize,
                        total: pagination.totalRecords,
                        showTotal: (total, range) =>
                          `Showing ${range[0]} to ${range[1]} of ${total} companies`,
                        onChange: (page) =>
                          setPagination((prev) => ({
                            ...prev,
                            pageNumber: page,
                          })),
                        showSizeChanger: false,
                      }
                    : undefined
                }
              />
            </div>
          )}
        </div>
      </div>
      <Notification
        display={notf.display}
        type={notf.type}
        message={notf.message}
        onClose={() => setNotf((prev) => ({ ...prev, display: false }))}
      />
      <ModalUserList
        title="User List"
        headers={userHeaders}
        keys={userKeys}
        open={dialogs.userList}
        list={users()}
        onCloseRequest={() =>
          setDialogs((prev) => ({ ...prev, userList: false }))
        }
      />
      <ModalList
        title="Store List"
        headers={storeHeaders}
        keys={storeKeys}
        size="lg"
        open={dialogs.storeList}
        list={selectedStoreList || []}
        onCloseRequest={() =>
          setDialogs((prev) => ({ ...prev, storeList: false }))
        }
      />
      <TWModal
        open={dialogs.upsert}
        onClose={onCloseUpsert}
        title={form.id ? 'Edit company' : 'Add company'}
        size="2xl"
        showCloseButton
        dismissible={false}
        panelClassName="dark:text-slate-200"
        bodyClassName="min-h-0 space-y-5 overflow-y-auto overscroll-contain px-4 py-5 sm:px-6 max-h-[70vh]"
        footer={
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="order-1 flex w-full flex-col gap-2 sm:order-2 sm:w-auto sm:flex-row sm:justify-end">
              <TWButton
                variant="default"
                label="Cancel"
                disabled={loading.upsertLoading}
                onClick={() => onCloseUpsert()}
                className="w-full sm:w-auto"
              />
              <TWButton
                variant="primary"
                label={form.id ? 'Save company' : 'Add company'}
                disabled={loading.upsertLoading}
                loading={loading.upsertLoading}
                onClick={() => onFormSubmit()}
                className="w-full sm:w-auto"
              />
            </div>
            {form.id && (
              <div className="order-2 flex w-full justify-stretch sm:order-1 sm:w-auto sm:justify-start">
                <TWButton
                  variant="danger"
                  icon={TrashIcon}
                  label="Delete company"
                  disabled={loading.deleteLoading}
                  loading={loading.deleteLoading}
                  onClick={() => onClickDelete()}
                  className="w-full sm:w-auto"
                />
              </div>
            )}
          </div>
        }
      >
        <div>
          <label
            htmlFor="logo"
            className="block pb-3 text-sm font-medium text-gray-700 dark:text-slate-200"
          >
            Company Logo
          </label>
          <div className="mt-5">
            <>
              <Upload
                multiple={false}
                maxCount={1}
                name="avatar"
                onChange={(e) => onFormChange(e.file, 'image')}
                listType="picture-circle"
                className="avatar-uploader"
                beforeUpload={() => false}
              >
                {form.logo ? (
                  <img
                    src={form.logo}
                    alt="avatar"
                    className="min-h-[4rem] min-w-[4rem] h-16 w-16 rounded-full"
                    style={{
                      width: '100%',
                      height: '100%',
                    }}
                  />
                ) : (
                  uploadButton
                )}
              </Upload>
            </>
            <div className="h-2 mt-1  text-xs">
              {errors.imageid && (
                <p className="text-red-700">Image can not be empty</p>
              )}
            </div>
          </div>
        </div>
        <div>
          <label
            htmlFor="name"
            className="block text-sm font-medium text-gray-700 dark:text-slate-400"
          >
            Company Name
          </label>
          <div className="mt-1">
            <input
              value={form.name}
              onChange={(e) => onFormChange(e.target.value, 'name')}
              type="text"
              name="name"
              id="name"
              className="block w-full appearance-none rounded-md border dark:text-slate-200 dark:bg-slate-800 border-gray-300 px-3 py-2 placeholder-black placeholder-opacity-25  focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm dark:focus:border-sky-400 dark:focus:ring-sky-400 dark:placeholder:text-white dark:border-slate-500 dark:placeholder:text-white"
              placeholder="e.g. 'Abc Pizza'"
            />
            <div className="h-2 mt-1  text-xs">
              {errors.name && (
                <p className="text-red-700">Name can not be empty</p>
              )}
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-4 sm:flex-row sm:gap-6">
          <div className="w-full sm:w-1/2">
            <label
              htmlFor="ownername"
              className="block text-sm font-medium text-gray-700 dark:text-slate-400"
            >
              Owner Name
            </label>
            <div className="mt-1">
              <input
                value={form.ownername}
                onChange={(e) => onFormChange(e.target.value, 'ownername')}
                type="text"
                name="ownername"
                id="ownername"
                className="block w-full appearance-none rounded-md border dark:text-slate-200 dark:bg-slate-800 border-gray-300 px-3 py-2 placeholder-black placeholder-opacity-25  focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm dark:focus:border-sky-400 dark:focus:ring-sky-400 dark:placeholder:text-white dark:border-slate-500"
                placeholder="e.g. 'John Doe'"
              />
              <div className="h-2 mt-1  text-xs">
                {errors.ownername && (
                  <p className="text-red-700">Owner name can not be empty</p>
                )}
              </div>
            </div>
          </div>
          <div className="w-full sm:w-1/2">
            <label
              htmlFor="ownerphone"
              className="block text-sm font-medium text-gray-700 dark:text-slate-400"
            >
              Owner Phone
            </label>
            <div className="mt-1">
              <ReactInputMask
                mask="(999) 999 9999"
                maskPlaceholder={' '}
                value={form.ownerphone}
                onChange={(e) => onFormChange(e.target.value, 'ownerphone')}
                type="text"
                name="ownerphone"
                id="ownerphone"
                className="block w-full appearance-none rounded-md border dark:text-slate-200 dark:bg-slate-800 border-gray-300 px-3 py-2 placeholder-black placeholder-opacity-25  focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm dark:focus:border-sky-400 dark:focus:ring-sky-400 dark:placeholder:text-white dark:border-slate-500"
              />
              <div className="h-2 mt-1  text-xs">
                {errors.ownerphone && (
                  <p className="text-red-700">Owner phone can not be empty</p>
                )}
              </div>
            </div>
          </div>
        </div>
        <div>
          <label
            htmlFor="owneremail"
            className="block text-sm font-medium text-gray-700 dark:text-slate-400"
          >
            Owner E-mail
          </label>
          <div className="mt-1">
            <input
              value={form.owneremail}
              onChange={(e) => onFormChange(e.target.value, 'owneremail')}
              type="text"
              name="owneremail"
              id="owneremail"
              className="block w-full appearance-none rounded-md border dark:text-slate-200 dark:bg-slate-800 border-gray-300 px-3 py-2 placeholder-black placeholder-opacity-25  focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm dark:focus:border-sky-400 dark:focus:ring-sky-400 dark:placeholder:text-white dark:border-slate-500 dark:placeholder:text-white"
              placeholder="e.g. 'owner@abc.com'"
            />
            <div className="h-2 mt-1  text-xs"></div>
          </div>
        </div>
        {roles.includes(100) && (
          <div className="">
            <label
              htmlFor="users"
              className="block text-sm font-medium text-gray-700 dark:text-slate-400"
            >
              Users
            </label>
            <Select
              mode="multiple"
              allowClear
              size="large"
              className="company-page-select w-full"
              style={{
                width: '100%',
              }}
              placeholder="Please select"
              defaultValue={form.users}
              onChange={(val) => onFormChange(val, 'users')}
              options={userList.map((x) => ({
                label: x.fullname,
                value: x.id,
              }))}
            />
          </div>
        )}

        {roles.includes(100) && (
          <div className="">
            <label
              htmlFor="subscription"
              className="block text-sm font-medium text-gray-700 dark:text-slate-400"
            >
              Subscription
            </label>
            <Select
              size="large"
              className="company-page-select w-full"
              style={{
                width: '100%',
              }}
              placeholder="Please select"
              defaultValue={form?.subscriptionid}
              onChange={(val) => onFormChange(val, 'subscriptionid')}
              options={subscriptionList.map((x) => ({
                label: x.name,
                value: x.id,
              }))}
            />
          </div>
        )}
        <div className="h-2 mt-1  text-xs"></div>
      </TWModal>
      <TWModal
        open={loading.loginAsCompany}
        onClose={() => {}}
        dismissible={false}
        size="sm"
      >
        <div className="flex flex-col items-center space-y-4 py-4">
          <h1>Logging in</h1>
          <Spin />
        </div>
      </TWModal>
      <ModalDelete
        title="Company"
        loading={loading.deleteLoading}
        open={dialogs.delete}
        onCloseRequest={() =>
          setDialogs((prev) => ({ ...prev, delete: false }))
        }
        onOk={onConfirmDelete}
        item={form}
        shouldConfirm={true}
      />
    </>
  );
};

export default Company;
